import {Button, Flash} from '@primer/react'
import {SkeletonText} from '@primer/react/experimental'
import {Link} from '@github-ui/react-core/link'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {useNavigate} from '@github-ui/use-navigate'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {useCallback, useState, type ReactNode} from 'react'
import {clsx} from 'clsx'

import type {SidebarConfigCallToActionProps} from '../types'
import styles from './SidebarConfigCallToAction.module.css'

/** Renders a call-to-action banner for special configuration repositories (profile READMEs). */
export function SidebarConfigCallToAction({
  onOptIn,
  ...props
}: SidebarConfigCallToActionProps & {onOptIn?: () => void}) {
  const {
    repoNameWithOwner,
    isPrivate,
    isOrganization,
    isOrgMemberProfile,
    optedIn,
    hasReadme,
    readmePath,
    addReadmePath,
    editReadmePath,
    settingsPath,
    profilePath,
    optInPath,
    ownerProfileName,
  } = props
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const navigate = useNavigate()
  const [optingIn, setOptingIn] = useState(false)

  const handleOptIn = useCallback(async () => {
    if (!optInPath) return
    setOptingIn(true)
    sendClickAnalyticsEvent({
      category: 'REPOSITORY_OVERVIEW',
      action: 'click',
      label: `location:sidebar;section:config_repo_cta;ref:${isPrivate ? 'update_now' : 'share_to_profile'}`,
    })
    try {
      const resp = await verifiedFetch(optInPath, {method: 'POST'})
      if (!resp.ok) return
      onOptIn?.()
      navigate(profilePath)
    } finally {
      setOptingIn(false)
    }
  }, [optInPath, isPrivate, sendClickAnalyticsEvent, onOptIn, profilePath, navigate])

  const sendCTAClick = useCallback(
    (ref: string) => {
      sendClickAnalyticsEvent({
        category: 'REPOSITORY_OVERVIEW',
        action: 'click',
        label: `location:sidebar;section:config_repo_cta;ref:${ref}`,
      })
    },
    [sendClickAnalyticsEvent],
  )

  let title: ReactNode
  let description: ReactNode
  let actions: ReactNode = null

  if (!optedIn) {
    title = isPrivate ? <span>is special.</span> : <span>is now a special repository.</span>
    description = isPrivate ? (
      <>
        If you <strong>make this a public repository</strong>, its <code>{readmePath}</code> will appear on your
        profile.
      </>
    ) : (
      <>You can display {readmePath} from this repository on your public GitHub profile.</>
    )
    actions = (
      <Button variant="primary" onClick={handleOptIn} loading={optingIn} disabled={optingIn}>
        {isPrivate ? 'Update now' : 'Share to Profile'}
      </Button>
    )
  } else if (isPrivate && !isOrgMemberProfile) {
    title = <span>is special.</span>
    description = (
      <>
        If you <strong>make this a public repository</strong>, its <code>{readmePath}</code> will appear on your
        profile.
      </>
    )
    actions = settingsPath ? (
      <Button as={Link} to={settingsPath} variant="primary" onClick={() => sendCTAClick('settings')}>
        Go to settings
      </Button>
    ) : null
  } else {
    title = <span>is a special repository.</span>

    if (isOrgMemberProfile && isPrivate) {
      const profileDisplay = ownerProfileName ?? "the organization's"
      description = hasReadme ? (
        <>
          The <code>{readmePath}</code> will appear on {profileDisplay} member profile, visible only to organization
          members.
        </>
      ) : (
        <>
          Create a <code>{readmePath}</code> and it will appear on {profileDisplay} member profile, visible only to
          organization members.
        </>
      )
    } else if (isOrganization) {
      description = hasReadme ? (
        <>
          The <code>{readmePath}</code> will appear on the organization&apos;s profile.
        </>
      ) : (
        <>
          Create a <code>{readmePath}</code> and it will appear on the organization&apos;s profile.
        </>
      )
    } else {
      description = (
        <>
          Its <code>{readmePath}</code> will appear on your public profile.
        </>
      )
    }

    actions =
      hasReadme && editReadmePath ? (
        <>
          <Button as={Link} to={editReadmePath} variant="primary" onClick={() => sendCTAClick('edit_readme')}>
            Edit README
          </Button>
          <Button as={Link} to={profilePath} onClick={() => sendCTAClick('visit_profile')}>
            Visit profile
          </Button>
        </>
      ) : (
        <Button as={Link} to={addReadmePath} variant="primary" onClick={() => sendCTAClick('add_readme')}>
          Add a README
        </Button>
      )
  }

  return (
    <Flash className={styles.callToAction} data-testid="config-repo-call-to-action">
      <div className={styles.content}>
        <p className={styles.contentTitle}>
          <strong>{repoNameWithOwner}</strong> {title}
        </p>
        <p className={styles.contentDescription}>{description}</p>
      </div>
      {actions && <div className={styles.actions}>{actions}</div>}
    </Flash>
  )
}

/** Skeleton placeholder that reserves space while the CTA data loads. */
export function SidebarConfigCallToActionSkeleton() {
  return (
    <Flash className={clsx(styles.callToAction, styles.skeleton)}>
      <SkeletonText lines={3} />
    </Flash>
  )
}
